import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import CloseIcon from "../../icons/CloseIcon";
import ImageCreateIcon from "../../icons/ImageCreateIcon";
import InfoCircleIcon from "../../icons/InfoCircleIcon";
import SendIcon from "../../icons/SendIcon";
import styles from "./messageComposer.module.css";

// The server's limits: 4000 characters (support chat and ticket schemas) and
// 5 MB per image (multer).
const MAX_LENGTH = 4000;
const COUNTER_FROM = 3500;
const MAX_HEIGHT = 160;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const TYPING_EVERY_MS = 2000;
const TYPING_IDLE_MS = 3000;

// Unsent text per conversation for as long as the dashboard stays open: a
// reply typed in one conversation never carries over to another.
const drafts = new Map();

// The reply box, mounted per conversation (keyed by it). onSend({ text,
// files }) shows the message in the thread at once; a failed send stays there
// with a retry, so nothing typed is lost.
//   draftKey: where this conversation's unsent text is kept
//   maxFiles: images allowed per message (0 = text only); pick, paste or drop
//   onTyping / onStopTyping: optional, throttled "typing" signals
const MessageComposer = ({
  draftKey,
  onSend,
  notice = "",
  placeholder = "اكتب ردّك…",
  maxFiles = 0,
  onTyping,
  onStopTyping,
}) => {
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [text, setText] = useState(() => drafts.get(draftKey) || "");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [fileError, setFileError] = useState("");
  const [dragging, setDragging] = useState(false);

  const trimmed = text.trim();
  const tooLong = text.length > MAX_LENGTH;
  const canSend = (trimmed.length > 0 || files.length > 0) && !tooLong;

  // A conversation just opened: ready to type.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Grows with the text up to MAX_HEIGHT, then scrolls.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, MAX_HEIGHT)}px`;
  }, [text]);

  useEffect(() => {
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  // "Typing": at most every TYPING_EVERY_MS, stopped after TYPING_IDLE_MS
  // idle, on send, on clearing the text and on leaving the conversation.
  const typing = useRef({ at: 0, idle: null, onTyping, onStopTyping });
  typing.current.onTyping = onTyping;
  typing.current.onStopTyping = onStopTyping;

  const stopTyping = useCallback(() => {
    const state = typing.current;
    clearTimeout(state.idle);
    if (!state.at) return;
    state.at = 0;
    state.onStopTyping?.();
  }, []);

  useEffect(() => stopTyping, [stopTyping]);

  const signalTyping = (value) => {
    const state = typing.current;
    if (!state.onTyping) return;
    if (!value.trim()) {
      stopTyping();
      return;
    }
    const now = Date.now();
    if (now - state.at > TYPING_EVERY_MS) {
      state.at = now;
      state.onTyping();
    }
    clearTimeout(state.idle);
    state.idle = setTimeout(stopTyping, TYPING_IDLE_MS);
  };

  const keepDraft = (value) => {
    if (value) drafts.set(draftKey, value);
    else drafts.delete(draftKey);
  };

  const update = (value) => {
    setText(value);
    keepDraft(value);
    signalTyping(value);
  };

  const addFiles = (picked) => {
    if (!maxFiles || picked.length === 0) return;
    const images = picked.filter((file) => IMAGE_TYPES.includes(file.type));
    const fitting = images.filter((file) => file.size <= MAX_IMAGE_BYTES);
    const next = [...files, ...fitting];
    let error = "";
    if (images.length < picked.length) error = "الصور فقط: JPG أو PNG أو WEBP.";
    else if (fitting.length < images.length) error = "حجم الصورة أكبر من 5 ميغابايت.";
    if (next.length > maxFiles) error = `حتى ${maxFiles} صور في الرسالة الواحدة.`;
    setFiles(next.slice(0, maxFiles));
    setFileError(error);
  };

  const removeFile = (index) => {
    setFiles((current) => current.filter((_, i) => i !== index));
    setFileError("");
  };

  const send = () => {
    if (!canSend) return;
    onSend({ text: trimmed, files });
    setText("");
    keepDraft("");
    setFiles([]);
    setFileError("");
    stopTyping();
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    send();
  };

  // A pasted screenshot attaches; pasted text pastes as usual.
  const handlePaste = (event) => {
    const pasted = Array.from(event.clipboardData?.files || []);
    if (!maxFiles || pasted.length === 0) return;
    event.preventDefault();
    addFiles(pasted);
  };

  const dropProps = maxFiles
    ? {
        onDragOver: (event) => {
          if (!event.dataTransfer?.types?.includes("Files")) return;
          event.preventDefault();
          setDragging(true);
        },
        onDragLeave: (event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
        },
        onDrop: (event) => {
          event.preventDefault();
          setDragging(false);
          addFiles(Array.from(event.dataTransfer?.files || []));
        },
      }
    : {};

  return (
    <div className={`${styles.composer} ${dragging ? styles.dragging : ""}`} {...dropProps}>
      {notice && (
        <div className={styles.notice} role="note">
          <InfoCircleIcon className={styles.noticeIcon} />
          <span>{notice}</span>
        </div>
      )}

      {files.length > 0 && (
        <div className={styles.files}>
          {files.map((file, index) => (
            <div key={`${file.name}-${file.size}-${index}`} className={styles.file}>
              {previews[index] && <img src={previews[index]} alt={file.name} />}
              <button
                type="button"
                className={styles.removeFile}
                onClick={() => removeFile(index)}
                aria-label="إزالة الصورة"
                title="إزالة الصورة"
              >
                <CloseIcon />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={styles.row}>
        {maxFiles > 0 && (
          <>
            <button
              type="button"
              className={styles.attach}
              onClick={() => fileInputRef.current?.click()}
              disabled={files.length >= maxFiles}
              aria-label="إرفاق صورة"
              title="إرفاق صورة"
            >
              <ImageCreateIcon />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={IMAGE_TYPES.join(",")}
              multiple
              hidden
              onChange={(event) => {
                addFiles(Array.from(event.target.files || []));
                event.target.value = "";
              }}
            />
          </>
        )}
        <textarea
          ref={inputRef}
          className={styles.input}
          dir="auto"
          rows={1}
          placeholder={placeholder}
          aria-label="نص الرد"
          value={text}
          onChange={(event) => update(event.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
        <button
          type="button"
          className={styles.send}
          onClick={send}
          disabled={!canSend}
          aria-label="إرسال"
          title="إرسال"
        >
          <SendIcon className={styles.sendIcon} />
        </button>
      </div>

      <div className={styles.hint}>
        {fileError ? (
          <span className={styles.error} role="alert">
            {fileError}
          </span>
        ) : (
          <span>
            <kbd dir="ltr">Enter</kbd> للإرسال · <kbd dir="ltr">Shift + Enter</kbd> لسطر جديد
            {maxFiles > 0 && " · الصق صورة أو اسحبها إلى هنا"}
          </span>
        )}
        {text.length >= COUNTER_FROM && (
          <span className={tooLong ? styles.over : undefined} dir="ltr">
            {text.length.toLocaleString("en")} / {MAX_LENGTH.toLocaleString("en")}
          </span>
        )}
      </div>
    </div>
  );
};

export default MessageComposer;
